import { useState } from "react";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/virtualizedTable";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useGetTemplateInstance,
  TaskTemplateInstance,
  useGetTemplate,
} from "@/lib/api/checklist/templates";
import { TaskInstance, InstanceStatus } from "@/lib/api/checklist";
import {
  ChevronLeft,
  User,
  Calendar,
  FileText,
  Tag,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowRightIcon
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useListMinimalUsers } from "@/lib/api/users";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { StatusIndicator } from "../components";
import { MetaCustomerInfoPreview } from "./MetaCustomerInfoPreview";
import { doesInstanceMetaHaveCustomerInfo, formatCustomerAddress, formatMailingAddress, META_DATA_KEY_EXCLUDE_LIST } from "./schemas";

interface TemplateInstanceDetailModalProps {
  open: boolean;
  onClose: () => void;
  templateInstanceId: string;
  templateId?: string;
  highlightTaskId?: string;
}


export function TemplateInstanceDetailModal({
  open,
  onClose,
  templateInstanceId,
  templateId,
  highlightTaskId,
}: TemplateInstanceDetailModalProps) {
  const instanceResult = useGetTemplateInstance(templateInstanceId);
  const templateResult = useGetTemplate(templateId || "");
  const usersMinimalListRes = useListMinimalUsers();
  const users = usersMinimalListRes?.data?.data || [];

  const instance = instanceResult?.data?.data;
  const template = templateResult?.data?.data;
  const isLoading = instanceResult.isLoading || (templateId && templateResult.isLoading);

  const formatDate = (dateString: string) => {
    return moment.tz(dateString, CommonDestinationTimezone).format("MMM DD, YYYY h:mm A");
  };

  const getUserName = (userId?: string): string => {
    if (!userId) return "Unassigned";
    const user = users.find((u: { id: string }) => u.id === userId);
    return user?.name || "Unknown";
  };

  const columnHelper = createColumnHelper<TaskInstance>();

  const columns = [
    columnHelper.accessor("task.title", {
      id: "title",
      header: "Task",
      meta: { isCellClick: true },
      cell: (info) => {
        const taskInstance = info.row.original;
        const isHighlighted = highlightTaskId === taskInstance.task_id;

        return (
          <div className={`font-medium ${isHighlighted ? 'p-2 bg-yellow-50 border-y-2 border-yellow-400 -mx-2' : ''}`}>
            {taskInstance.task?.title || "Unknown Task"}
            {taskInstance.task?.description && (
              <p className="text-sm text-muted-foreground truncate max-w-md">
                {taskInstance.task.description}
              </p>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: "Status",
      cell: (info) => <StatusIndicator status={info.getValue()} secondary_status={info.row.original.secondary_status} />,
    }),
    columnHelper.accessor("assigned_to_id", {
      id: "assigned_to",
      header: "Assigned To",
      ...({ accessorFn: (row: any) => getUserName(row.assigned_to_id) }),
      cell: (info: any) => {
        return (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{info.getValue()}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor("due_date", {
      id: "due_date",
      header: "Due Date",
      filterFn: "dateTimeFilter" as any,
      cell: (info) => {
        const taskInstance = info.row.original;
        const isLate = taskInstance.is_late;

        return (
          <div className="flex items-center gap-2">
            {isLate ? (
              <AlertCircle className="h-4 w-4 text-destructive" />
            ) : (
              <Clock className="h-4 w-4 text-muted-foreground" />
            )}
            <span className={`text-sm ${isLate ? 'text-destructive' : ''}`}>
              {formatDate(info.getValue())}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor("completed_at", {
      id: "completed_at",
      header: "Completed",
      filterFn: "dateTimeFilter" as any,
      cell: (info) => {
        const completedAt = info.getValue();
        if (!completedAt) return <span className="text-muted-foreground">-</span>;

        return (
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-sm">{formatDate(completedAt)}</span>
          </div>
        );
      },
    }),
  ];

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[90vw] max-h-[96vh] min-w-[90vw]  min-h-[96vh] overflow-hidden flex flex-col">
        <DialogHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8 -ml-2"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <DialogTitle className="text-xl">
              Template Instance Details
            </DialogTitle>
          </div>
          <DialogDescription className="text-base">
            View all tasks created from this template instance
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : instance ? (
          <div className="flex-1 overflow-hidden flex flex-col mt-4">
            <div className="space-y-6 px-1">
              {/* Instance Information */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">
                    {template?.name || instance.template?.name || "Unknown Template"}
                  </h3>
                  {(template?.description || instance.template?.description) && (
                    <p className="text-sm text-muted-foreground">
                      {template?.description || instance.template?.description}
                    </p>
                  )}
                </div>

                {/* Instance summary bar */}
                <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border mb-4">
                  <div className="flex items-center gap-6 lg:grid lg:grid-cols-3 w-full">
                    <div className="space-y-1">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Triggered At
                      </span>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">
                          {formatDate(instance.created_at)}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1 grid">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Tasks Created
                      </span>
                      <Badge variant="secondary" className="w-fit">
                        {instance.task_instances?.length || 0} tasks
                      </Badge>
                    </div>

                    {/* Always show triggered by in the summary bar */}
                    {instance.triggered_by && (
                      <div className="space-y-1">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Triggered By
                        </span>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">
                            {instance.triggered_by_user?.name || getUserName(instance.triggered_by)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Customer information for customer onboarding */}
                {instance.trigger_meta_data &&
                  doesInstanceMetaHaveCustomerInfo(instance.trigger_meta_data) && (
                    <MetaCustomerInfoPreview instance={instance.trigger_meta_data || {}} />
                  )}

                {instance.trigger_meta_data && instance.trigger_meta_data.trigger_type === "customer_updated" && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{instance.trigger_meta_data.customer_name} Updated</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {instance.trigger_meta_data.differences.map((difference: { key: string, new: string, original: string }, index: number) => (
                        <div
                          key={`difference_${index}`}
                          className="bg-muted/10 rounded-lg border border-border p-3 space-y-1"
                        >
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {difference.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </div>
                          <div className="text-sm font-medium break-words flex items-center gap-2">
                            {difference.original != null && (

                              <>
                                {prettyPrint(difference.original, getDisplayStyling(difference.new, difference.original))} <ArrowRightIcon className="h-4 w-4 text-muted-foreground" />
                              </>

                            )}
                            {prettyPrint(difference.new, getDisplayStyling(difference.original, difference.new))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Other metadata for non-customer onboarding */}
                {(instance.trigger_meta_data && instance.trigger_meta_data.trigger_type !== "customer_updated") &&
                  Object.keys(instance.trigger_meta_data).filter(key => !META_DATA_KEY_EXCLUDE_LIST.includes(key)).length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-3">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Additional Information</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(instance.trigger_meta_data).filter(([key]) => !META_DATA_KEY_EXCLUDE_LIST.includes(key)).sort((a, b) => a[0].localeCompare(b[0])).map(([key, value]) => (
                          <div
                            key={key}
                            className="bg-muted/10 rounded-lg border border-border p-3 space-y-1"
                          >
                            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                            <div className="text-sm font-medium break-words">
                              {typeof value === 'object' ? (
                                <span className="text-muted-foreground italic">
                                  {JSON.stringify(value, null, 2)}
                                </span>
                              ) : (
                                String(value)
                              )}
                            </div>
                          </div>
                        ))}
                        {(["new_customer_form", "new_non_customer_form", "customer_onboarding","non_customer_onboarding", "destination_setup"].includes(instance.trigger_meta_data?.trigger_type || "")) && (
                          ["customer_address", "mailing_address"].map((address_type) => {
                            const address = address_type === "customer_address" ? formatCustomerAddress(instance.trigger_meta_data) : formatMailingAddress(instance.trigger_meta_data);
                            return (
                              <div className="bg-muted/10 rounded-lg border border-border p-3 space-y-1">
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                  {address_type === "customer_address" ? instance.trigger_meta_data.trigger_type === "destination_setup" ? "Destination Location" : "Customer Address" : "Mailing Address"}
                                </div>
                                <div className="text-sm font-medium break-words">
                                  {address}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
              </div>

              {/* Task Instances Table */}
              <div className="flex-1 overflow-hidden flex flex-col">
                <h4 className="text-md font-semibold mb-3">
                  Task Instances ({instance.task_instances?.length || 0})
                </h4>

                {instance.task_instances && instance.task_instances.length > 0 ? (
                  <DataTable
                    columns={columns as any[]}
                    data={instance.task_instances}
                    height="430px"
                    className="max-h-[600px]"
                    defaultSorting={[{ id: "due_date", desc: false }]}
                  />
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-20" />
                    <p>No task instances found</p>
                  </div>
                )}

              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Template instance not found
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function prettyPrint(value: string, styling?: "green" | "red" | "orange") {
  const displayedValue = (() => {
    if (value == null) return "<Removed>";
    if (typeof value === 'boolean') return value === true ? "Yes" : "No";
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return value.replace(/\\n/g, '\n');
  })();

  switch (styling) {
    case "green":
      return <span className="text-green-500">{displayedValue}</span>;
    case "red":
      return <span className="text-red-500">{displayedValue}</span>;
    case "orange":
      return <span className="text-blue-500">{displayedValue}</span>;
    default:
      return <span>{displayedValue}</span>;
  }
}

function getDisplayStyling(before: any, after: any): "green" | "red" | "orange" | undefined {
  if (before == null && after != null) return "green";
  if (before != null && after == null) return "red";
  if (before != null && after != null) return "orange";
  return undefined;
}
